import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Button, Spinner, Alert, Container, Row, Col } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import JobSeekerNavbar from "../../Pages/Navbar/AgencyNavbar";
import Footer from "../../Footer/Footer";
import BaseUrl from "../../ApiServices/BaseUrl";

const ArticleDetailAgency = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch article by ID
  const fetchArticle = async () => {
    try {
      setLoading(true);
      console.log("Fetching article with ID:", id);

      const response = await fetch(`${BaseUrl}/api/news/${id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Fetched article data:", data);
      setArticle(data);

    } catch (err) {
      setError(err.message);
      console.error("Error fetching article:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchArticle();
    }
  }, [id]);

  // Format date function
  const formatDate = dateString => {
    const options = { day: "numeric", month: "short", year: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  if (loading) {
    return (
      <>
        <JobSeekerNavbar />
        <Container className="mt-4" style={{ paddingTop: "80px" }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </Container>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <JobSeekerNavbar />
        <Container className="mt-4" style={{ paddingTop: "80px" }}>
          <Alert variant="danger">
            Error loading article: {error}
            <div className="mt-3">
              <Button variant="primary" onClick={() => navigate(-1)}>
                Go Back
              </Button>
            </div>
          </Alert>
        </Container>
        <Footer />
      </>
    );
  }

  if (!article) {
    return (
      <>
        <JobSeekerNavbar />
        <Container className="mt-4" style={{ paddingTop: "80px" }}>
          <Alert variant="warning">
            Article not found.
            <div className="mt-3">
              <Button variant="primary" onClick={() => navigate(-1)}>
                Go Back
              </Button>
            </div>
          </Alert>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <JobSeekerNavbar />
      <Container className="mt-4" style={{ paddingTop: "80px" }}>
        <Button 
          variant="outline-primary" 
          className="mb-4"
          onClick={() => navigate(-1)}
        >
          <i className="bi bi-arrow-left"></i> Back to News
        </Button>

        <Card className="shadow-sm">
          {article.image_url && (
            <Card.Img
              variant="top"
              src={article.image_url}
              alt={article.title}
              style={{ maxHeight: "400px", objectFit: "cover" }}
              onError={e => (e.target.src = "/placeholder-image.jpg")}
            />
          )}
          
          <Card.Body className="p-4">
            <div className="d-flex justify-content-between text-muted small mb-3">
              <span>
                <i className="bi bi-calendar-event me-1"></i> 
                {formatDate(article.publish_date)}
              </span>
              <span>
                <i className="bi bi-clock me-1"></i> 
                {article.read_time || "5 min read"}
              </span>
              {article.category && (
                <span className="badge bg-primary">{article.category}</span>
              )}
            </div>

            <Card.Title className="fw-bold text-primary mb-3">
              {article.title}
            </Card.Title>

            {article.excerpt && (
              <Card.Text className="lead text-muted mb-4">
                {article.excerpt}
              </Card.Text>
            )}

            <div className="article-content">
              {article.content ? (
                <div 
                  dangerouslySetInnerHTML={{ __html: article.content }} 
                  className="text-justify"
                />
              ) : (
                <Alert variant="info">No content available for this article.</Alert>
              )}
            </div>

            {article.tags && (
              <div className="mt-4 pt-3 border-top">
                <strong>Tags: </strong>
                {article.tags.split(',').map((tag, index) => (
                  <span key={index} className="badge bg-secondary me-1">
                    {tag.trim()}
                  </span>
                ))}
              </div>
            )}

            {article.meta_description && (
              <div className="mt-3 p-3 bg-light rounded">
                <small className="text-muted">
                  <strong>Summary: </strong>
                  {article.meta_description}
                </small>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
      <Footer />
    </>
  );
};

export default ArticleDetailAgency;